import { useMemo } from "react";
import DataTable from "../DataTable/DataTable.js";
import FilterModal from "./FilterModal.js";
import {
  filterTableData,
  getFlatTableData,
  getFilterOptions as getFilterOptionsUtil,
  getAllFilterOptions as getAllFilterOptionsUtil,
} from "../../utils/filters.js";
import filterIcon from "../../vendor/filter.svg";

const RespondsTable = ({
  isAdmin,
  data,
  intervals,
  showDaily,
  onSort,
  onFilterChange,
  onClearFilter,
  filters,
  sortBy,
  sortOrder,
  showFilterModal,
  setShowFilterModal,
  setFilters,
}) => {
  const filteredData = useMemo(
    () => filterTableData(data, filters, isAdmin),
    [data, filters, isAdmin]
  );

  const tableData = useMemo(() => {
    const flatData = getFlatTableData(filteredData, isAdmin);

    return flatData.sort((a, b) => {
      let valueA, valueB;

      switch (sortBy) {
        case "ink":
          valueA = a.ink;
          valueB = b.ink;
          break;
        case "company":
          valueA = a.company.toLowerCase();
          valueB = b.company.toLowerCase();
          break;
        case "vacancy":
          valueA = a.vacancy.toLowerCase();
          valueB = b.vacancy.toLowerCase();
          break;
        case "manager":
          valueA = a.manager.toLowerCase();
          valueB = b.manager.toLowerCase();
          break;
        case "responds":
          valueA = a.respondCount;
          valueB = b.respondCount;
          break;
        default:
          return 0;
      }

      let result = 0;
      if (typeof valueA === "number" && typeof valueB === "number") {
        result = valueA - valueB;
      } else {
        result = valueA.localeCompare(valueB);
      }

      return sortOrder === "desc" ? -result : result;
    });
  }, [filteredData, sortBy, sortOrder, isAdmin]);

  const totals = useMemo(() => {
    if (!tableData.length || !intervals.length) return null;

    const uniqueCompanies = new Set(tableData.map((item) => item.company));
    const uniqueVacancies = new Set(tableData.map((item) => item.vacancy));
    const uniqueManagers = new Set(tableData.map((item) => item.manager));

    const intervalTotals = intervals.map((_, intervalIndex) => {
      return tableData.reduce((sum, item) => {
        const intervalCount = item.intervalCounts?.[intervalIndex] || 0;
        return sum + intervalCount;
      }, 0);
    });

    const totalResponds = tableData.reduce(
      (sum, item) => sum + (item.respondCount || 0),
      0
    );

    const intervalSourcesTotals = intervals.map((_, intervalIndex) => {
      const sources = { avito: 0, yandex: 0, vk: 0 };

      tableData.forEach((item) => {
        const intervalSources = item.intervalSources?.[intervalIndex];
        if (intervalSources) {
          Object.entries(intervalSources).forEach(([source, count]) => {
            if (sources.hasOwnProperty(source)) {
              sources[source] += count || 0;
            }
          });
        }
      });

      return sources;
    });

    const totalSources = { avito: 0, yandex: 0, vk: 0 };
    tableData.forEach((item) => {
      if (item.responds) {
        item.responds.forEach((respond) => {
          if (respond?.source) {
            const source = respond.source.toLowerCase();
            if (totalSources.hasOwnProperty(source)) {
              totalSources[source]++;
            }
          }
        });
      }
    });

    return {
      companies: uniqueCompanies.size,
      vacancies: uniqueVacancies.size,
      managers: uniqueManagers.size,
      intervalTotals,
      totalResponds,
      intervalSourcesTotals,
      totalSources,
    };
  }, [tableData, intervals]);

  const filterOptions = useMemo(
    () => getFilterOptionsUtil(data, filters, isAdmin),
    [data, filters, isAdmin]
  );

  const getAllFilterOptions = useMemo(
    () => getAllFilterOptionsUtil(data, isAdmin),
    [data, isAdmin]
  );

  const renderSortArrow = (column) => {
    if (sortBy !== column) return null;
    return sortOrder === "desc" ? " ↑" : " ↓";
  };

  const renderFilterButton = (column) => {
    const hasFilter = filters[column].length > 0;
    return (
      <img
        className="icon"
        onClick={() => setShowFilterModal(column)}
        style={{
          marginLeft: "10px",
          width: "17px",
          height: "15px",
          cursor: "pointer",
        }}
        src={filterIcon}
        alt="Фильтр"
        title={`Фильтр ${hasFilter ? `(${filters[column].length})` : ""}`}
      />
    );
  };

  return (
    <>
      <DataTable
        isAdmin={isAdmin}
        tableData={tableData}
        intervals={intervals}
        totals={totals}
        sortBy={sortBy}
        sortOrder={sortOrder}
        handleSort={onSort}
        renderSortArrow={renderSortArrow}
        renderFilterButton={renderFilterButton}
        showDaily={showDaily}
      />

      <FilterModal
        showFilterModal={showFilterModal}
        setShowFilterModal={setShowFilterModal}
        filters={filters}
        setFilters={setFilters}
        handleFilterChange={onFilterChange}
        clearFilter={onClearFilter}
        getAllFilterOptions={getAllFilterOptions}
        filterOptions={filterOptions}
      />
    </>
  );
};

export default RespondsTable;
